import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MdSearch, MdPrint, MdTrackChanges, MdEdit, MdCheck, MdClose, MdTrendingUp, MdCalendarMonth, MdPerson } from 'react-icons/md';
import api from '../api/axios';

export default function CenterTargetManagement() {
    const [targets, setTargets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [activeSubTab, setActiveSubTab] = useState('daily'); // 'monthly' or 'daily'
    
    // Filter States
    const now = new Date();
    const [selectedYear, setSelectedYear] = useState(now.getFullYear());
    const [selectedMonth, setSelectedMonth] = useState(now.getMonth() + 1); // 1-12
    const [selectedDate, setSelectedDate] = useState(now.toISOString().split('T')[0]);
    const [isSearchActive, setIsSearchActive] = useState(false);

    const MONTHS = [
        "JANUARY", "FEBRUARY", "MARCH", "APRIL", "MAY", "JUNE", 
        "JULY", "AUGUST", "SEPTEMBER", "OCTOBER", "NOVEMBER", "DECEMBER"
    ];

    const fetchData = async () => {
        setLoading(true);
        try {
            const endpoint = activeSubTab === 'monthly' ? '/target-stats' : '/daily-sheet-stats';
            const params = activeSubTab === 'monthly' 
                ? `?year=${selectedYear}&month=${selectedMonth}` 
                : `?date=${selectedDate}`;
                
            const res = await api.get(`${endpoint}${params}`);
            setTargets(res.data.stats || []);
        } catch (error) {
            console.error("Failed to fetch center target data", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [selectedYear, selectedMonth, selectedDate, activeSubTab]);

    const getRowColor = (percent, achieved) => {
        if (percent >= 100) return { bg: '#15803d', text: 'white' }; // Green-700
        if (percent >= 90)  return { bg: '#16a34a', text: 'white' }; // Green-600
        if (percent >= 70)  return { bg: '#2563eb', text: 'white' }; // Blue-600
        if (percent >= 50)  return { bg: '#ca8a04', text: 'white' }; // Amber-600
        if (percent >= 30)  return { bg: '#ea580c', text: 'white' }; // Orange-600
        
        if (achieved >= 4) return { bg: '#7e22ce', text: 'white' }; 
        if (achieved === 3) return { bg: '#9333ea', text: 'white' }; 
        if (achieved === 2) return { bg: '#a855f7', text: 'white' }; 
        if (achieved === 1) return { bg: '#c084fc', text: 'white' }; 
        
        return { bg: '#dc2626', text: 'white' };                   
    };

    const filtered = targets.filter(t => 
        (t.name || '').toLowerCase().includes(searchTerm.toLowerCase())
    ).sort((a, b) => (b.total_admissions || 0) - (a.total_admissions || 0));

    const totalTarget = filtered.reduce((acc, t) => acc + (t.target || 0), 0);
    const totalAchieved = filtered.reduce((acc, t) => acc + (t.achieved || 0), 0);
    const totalAllAdmissions = filtered.reduce((acc, t) => acc + (t.total_admissions || 0), 0);
    const totalEarnings = filtered.reduce((acc, t) => acc + (t.earnings || 0), 0);
    const totalRemaining = totalTarget - totalAchieved;
    const totalPercent = totalTarget > 0 ? (totalAchieved / totalTarget * 100).toFixed(2) : 0;

    return (
        <div className="fade-in w-full h-full flex flex-col overflow-hidden bg-white" style={{ fontFamily: "'Roboto', sans-serif !important" }}>
            {/* Refined Toolbar */}
            {/* Refined Responsive Toolbar */}
            <div className="bg-white border-b border-slate-200 p-2 sm:p-4 flex flex-col sm:flex-row justify-between gap-3">
                {/* Top Row on Mobile: Icon and Switcher */}
                <div className="flex items-center justify-between sm:justify-start w-full sm:w-auto gap-2">
                    {/* Main Icon hidden on very small screens, visible on landscape/tablet */}
                    <div className="bg-slate-100 p-2 rounded-lg border border-slate-200 print:hidden hidden sm:block shrink-0">
                        <MdTrackChanges size={20} className="text-slate-600" />
                    </div>

                    <div className="flex bg-slate-100 p-1 rounded-lg border border-slate-200 print:hidden w-full sm:w-auto shrink-0">
                        <button 
                            onClick={() => setActiveSubTab('daily')}
                            className={`flex-1 sm:flex-none px-2 sm:px-3 py-1.5 rounded-md text-[10px] whitespace-nowrap font-black uppercase tracking-widest transition-all flex items-center justify-center gap-1.5 ${activeSubTab === 'daily' ? 'bg-blue-600 text-white shadow-md' : 'text-slate-500 hover:bg-slate-200'}`}
                        >
                            <MdTrendingUp size={14} className="shrink-0" /> <span className="whitespace-nowrap">Daily Target</span>
                        </button>
                        <button 
                            onClick={() => setActiveSubTab('monthly')}
                            className={`flex-1 sm:flex-none px-2 sm:px-3 py-1.5 rounded-md text-[10px] whitespace-nowrap font-black uppercase tracking-widest transition-all flex items-center justify-center gap-1.5 ${activeSubTab === 'monthly' ? 'bg-blue-600 text-white shadow-md' : 'text-slate-500 hover:bg-slate-200'}`}
                        >
                            <MdCalendarMonth size={14} className="shrink-0" /> <span className="whitespace-nowrap">Monthly Target</span>
                        </button>
                    </div>
                </div>

                {/* Bottom Row on Mobile: Filters, Search, Print */}
                <div className="flex items-center justify-between sm:justify-end gap-2 w-full sm:w-auto print:hidden">
                    {/* Search / Filters Container */}
                    <div className="flex-1 sm:flex-none" style={{ minWidth: isSearchActive ? '40%' : 'auto' }}>
                        <AnimatePresence mode="wait">
                            {isSearchActive ? (
                                <motion.div 
                                    key="search-bar"
                                    initial={{ width: 0, opacity: 0 }}
                                    animate={{ width: '100%', opacity: 1 }}
                                    exit={{ width: 0, opacity: 0 }}
                                    className="relative flex items-center w-full"
                                >
                                    <div className="relative w-full">
                                        <MdSearch className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                                        <input 
                                            autoFocus
                                            type="text" 
                                            placeholder="Search name..." 
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                            className="w-full py-1.5 pl-8 pr-3 border border-slate-300 rounded-md text-[11px] outline-none bg-slate-50 focus:border-blue-500"
                                        />
                                    </div>
                                </motion.div>
                            ) : (
                                <motion.div 
                                    key="filters"
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    className="flex items-center gap-1.5 w-full"
                                >
                                    {/* Month/Date Filters */}
                                    {activeSubTab === 'monthly' ? (
                                        <>
                                            <select 
                                                value={selectedMonth}
                                                onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                                                className="flex-1 sm:flex-none py-1 px-1.5 text-[9px] font-bold border border-slate-200 rounded-md bg-slate-50 outline-none"
                                            >
                                                {MONTHS.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
                                            </select>
                                            <select 
                                                value={selectedYear}
                                                onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                                                className="flex-1 sm:flex-none py-1 px-1.5 text-[9px] font-bold border border-slate-200 rounded-md bg-slate-50 outline-none"
                                            >
                                                {[2024, 2025, 2026].map(y => <option key={y} value={y}>{y}</option>)}
                                            </select>
                                        </>
                                    ) : (
                                        <div className="flex-1 sm:flex-none relative flex items-center gap-1.5 bg-slate-50 px-2 py-1.5 border border-slate-200 rounded-md w-full">
                                            <MdCalendarMonth size={14} className="text-blue-600 shrink-0" />
                                            <input 
                                                type="date" 
                                                value={selectedDate} 
                                                onChange={(e) => setSelectedDate(e.target.value)}
                                                className="border-none bg-transparent text-[11px] font-bold outline-none text-slate-800 w-full"
                                            />
                                        </div>
                                    )}
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex shrink-0 items-center gap-1.5">
                        <button 
                            onClick={() => { setIsSearchActive(!isSearchActive); if(isSearchActive) setSearchTerm(''); }} 
                            className={`p-1.5 border rounded-md transition-all flex items-center justify-center h-[30px] w-[30px] ${isSearchActive ? 'bg-red-100 border-red-200 text-red-500' : 'bg-slate-50 border-slate-200 text-slate-500'}`}
                        >
                            {isSearchActive ? <MdClose size={18} /> : <MdSearch size={18} />}
                        </button>

                        {/* Print Button */}
                        <button onClick={() => window.print()} className="h-[30px] px-2 sm:px-3 bg-blue-900 text-white rounded-md font-bold text-[10px] tracking-widest flex items-center justify-center gap-1.5 uppercase shrink-0">
                            <MdPrint size={14}/> <span className="hidden xs:inline sm:inline">Print</span>
                        </button>
                    </div>
                </div>
            </div>

            {/* Performance Sheet - Full Screen Width */}
            <div className="flex-1 overflow-y-auto w-full">
                {loading ? (
                    <div className="flex flex-col items-center justify-center py-32">
                        <div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mb-3"></div>
                        <p className="text-blue-400 font-bold uppercase tracking-widest text-[9px]">Loading Sheet Data...</p>
                    </div>
                ) : (
                    <div className="w-full bg-white border-t border-blue-900 overflow-hidden">
                        
                        {/* Blue Header Section */}
                        <div className="bg-blue-600 text-white py-3 px-4 border-b border-blue-800">
                            <h2 className="text-[14px] font-black uppercase tracking-[0.2em] text-center no-cursive shadow-sm">
                                {activeSubTab === 'monthly' 
                                    ? `${MONTHS[selectedMonth - 1]} ADMISSION ANALYSIS REPORT SHEET ${selectedYear}`
                                    : `DAILY ADMISSION TARGET TRACKING SHEET - ${selectedDate.split('-').reverse().join('/')}`
                                }
                            </h2>
                        </div>

                        <div className="overflow-x-auto overflow-y-hidden">
                            <table className="w-full border-collapse">
                                <thead>
                                    <tr className="bg-blue-800 text-white whitespace-nowrap">
                                        <th className="px-4 py-3 text-left font-black uppercase text-[11px] w-[20%] min-w-[150px] no-cursive border border-white/20">MOBILIZATION</th>
                                        <th className="px-3 py-3 text-center font-black uppercase text-[11px] no-cursive border border-white/20">Target</th>
                                        <th className="px-3 py-3 text-center font-black uppercase text-[11px] no-cursive border border-white/20">
                                            {activeSubTab === 'daily' ? 'Today Admission' : 'Total Admission'}
                                        </th>
                                        <th className="px-3 py-3 text-center font-black uppercase text-[11px] no-cursive border border-white/20">Remaining</th>
                                        <th className="px-3 py-3 text-center font-black uppercase text-[11px] no-cursive border border-white/20">Percent %</th>
                                        <th className="px-3 py-3 text-center font-black uppercase text-[11px] no-cursive border border-white/20">Earning</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filtered.map((row) => {
                                        const remaining = (row.target || 0) - (row.achieved || 0);
                                        const percent = row.target > 0 ? (row.achieved / row.target * 100) : 0;
                                        const colors = getRowColor(percent, row.achieved);

                                        return (
                                            <tr key={row.id} style={{ background: colors.bg, color: colors.text }} className="h-9 transition-all hover:brightness-105 whitespace-nowrap">
                                                <td className="px-4 py-1.5 text-left uppercase text-[12px] no-cursive border border-black/10">{row.name}</td>
                                                <td className="px-3 py-1.5 text-center text-[13px] no-cursive border border-black/10">{row.target}</td>
                                                <td className={`px-3 py-1.5 text-center text-[13px] no-cursive border border-black/10 ${activeSubTab === 'daily' ? 'font-bold bg-yellow-100 text-yellow-800' : ''}`}>
                                                    {row.total_admissions}
                                                </td>
                                                <td className="px-3 py-1.5 text-center text-[13px] no-cursive border border-black/10">{remaining < 0 ? 0 : remaining}</td>
                                                <td className="px-3 py-1.5 text-center text-[13px] no-cursive border border-black/10">{percent.toFixed(1)}%</td>
                                                <td className="px-3 py-1.5 text-center text-[13px] no-cursive border border-black/10">₹{row.earnings?.toLocaleString()}</td>
                                            </tr>
                                        );
                                    })}

                                    {/* Grand Total Row */}
                                    <tr className="bg-yellow-400 text-blue-900 h-9 whitespace-nowrap">
                                        <td className="px-4 py-1.5 text-left uppercase tracking-widest text-[12px] no-cursive border border-black/10">TOTAL ANALYSIS REVIEW</td>
                                        <td className="px-3 py-1.5 text-center text-[13px] no-cursive border border-black/10">{totalTarget}</td>
                                        <td className={`px-3 py-1.5 text-center text-[13px] no-cursive border border-black/10 ${activeSubTab === 'daily' ? 'font-bold' : ''}`}>
                                            {totalAllAdmissions}
                                        </td>
                                        <td className="px-3 py-1.5 text-center text-[13px] no-cursive border border-black/10">{totalRemaining < 0 ? 0 : totalRemaining}</td>
                                        <td className="px-3 py-1.5 text-center text-[13px] no-cursive border border-black/10">{totalPercent}%</td>
                                        <td className="px-3 py-1.5 text-center bg-blue-800 text-white text-[13px] no-cursive border border-black/20">₹{totalEarnings?.toLocaleString()}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                    </div>
                )}
            </div>

            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Roboto:wght@300;400;500;700;900&display=swap');
                
                * { font-family: 'Roboto', sans-serif !important; }
                .no-cursive { font-style: normal !important; text-decoration: none !important; }
                
                @media print {
                    .fade-in { background: white !important; }
                    header, nav, button, select, input, .print\\:hidden, .bg-[#f0f9ff] { display: none !important; }
                    .main-content { margin: 0 !important; }
                    .bg-white { border: none !important; }
                    .w-full { border: 1px solid #1e3a8a !important; }
                    .bg-blue-900 { background-color: #1e3a8a !important; -webkit-print-color-adjust: exact; }
                    .bg-yellow-400 { background-color: #facc15 !important; -webkit-print-color-adjust: exact; }
                    .bg-blue-800 { background-color: #1e40af !important; -webkit-print-color-adjust: exact; }
                    @page { margin: 0.5cm; size: landscape; }
                }

                input[type="number"]::-webkit-inner-spin-button, 
                input[type="number"]::-webkit-outer-spin-button { 
                    -webkit-appearance: none; 
                    margin: 0; 
                }

                ::-webkit-scrollbar { width: 5px; height: 5px; }
                ::-webkit-scrollbar-track { background: transparent; }
                ::-webkit-scrollbar-thumb { background: #bfdbfe; border-radius: 10px; }
            `}</style>
        </div>
    );
}
